import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, Output, ViewChild } from '@angular/core';
import { StripeCardSavedEvent, StripeCardService } from 'src/app/_services/stripe-card.service';

let instanceCounter = 0;

export { StripeCardSavedEvent };

/** Self-contained "Add card" widget — asks the backend for a Stripe SetupIntent
 *  (`StripeCardService.createSetupIntent()`), mounts a Stripe Elements card field using the
 *  publishable key that call returns, confirms the SetupIntent with the entered card, then tells
 *  the backend which PaymentMethod to keep on file (`StripeCardService.attachPaymentMethod()`) so
 *  it can persist the card's brand/last4/expiry. Has no dependency on business-setup state, so it
 *  can be dropped into any template in the app (`<app-stripe-add-card (cardSaved)="...">`)
 *  without extra wiring. Each instance gets its own DOM id so more than one can exist on a page
 *  at once. */
@Component({
  selector: 'app-stripe-add-card',
  templateUrl: './stripe-add-card.component.html',
  styleUrls: ['./stripe-add-card.component.scss']
})
export class StripeAddCardComponent implements AfterViewInit, OnDestroy {
  @Output() cardSaved = new EventEmitter<StripeCardSavedEvent>();
  @Output() cancelled = new EventEmitter<void>();

  @ViewChild('cardElementHost', { static: false }) cardElementHost?: ElementRef<HTMLDivElement>;

  readonly elementId = `stripe-add-card-el-${++instanceCounter}`;
  isCardComplete = false;
  isCardFocused = false;
  cardBrand = 'unknown';
  cardErrorText = '';
  isInitializing = true;
  isSaving = false;
  isSaved = false;
  mountError = '';

  // Billing details forwarded to Stripe as part of the PaymentMethod (confirmCardSetup's
  // billing_details) — not sent to our own backend directly, attachPaymentMethod() still only
  // ever sends the resulting paymentMethodId. Matches the fields Stripe's own hosted Payment
  // Element collects, so this reads as a real billing form rather than a bare card-number box.
  fullName = '';
  addressLine1 = '';

  private stripe: any = null;
  private elements: any = null;
  private cardElement: any = null;
  private clientSecret = '';

  constructor(private readonly stripeCardService: StripeCardService) {}

  ngAfterViewInit(): void {
    // Defer to the next tick so the host div (behind *ngIf toggles in a consuming template) is
    // guaranteed to be in the DOM before Stripe.js tries to mount into it.
    setTimeout(() => this.startSetupIntent());
  }

  private startSetupIntent(): void {
    this.isInitializing = true;
    this.stripeCardService.createSetupIntent().subscribe({
      next: (result) => {
        this.isInitializing = false;
        if (!result.success || !result.data?.clientSecret || !result.data?.publishableKey) {
          this.mountError = result.message || 'Unable to start card setup.';
          return;
        }
        this.clientSecret = result.data.clientSecret;
        // isInitializing flipping to false here only updates the component's own field — Angular
        // hasn't re-rendered the template yet (we're still inside the HTTP subscribe callback), so
        // the *ngIf-gated #cardElementHost div doesn't exist in the DOM until the next change
        // detection pass. Mounting synchronously here left cardElementHost undefined and silently
        // no-opped (mountCardElement's `if (!this.cardElementHost) return;`), rendering an empty
        // box with no Stripe iframe and no visible error. Deferring to a fresh macrotask (same
        // trick as ngAfterViewInit above) guarantees that render has happened first.
        setTimeout(() => this.mountCardElement(result.data.publishableKey));
      },
      error: () => {
        this.isInitializing = false;
        this.mountError = 'Unable to start card setup. Please try again.';
      }
    });
  }

  private mountCardElement(publishableKey: string, attempt: number = 0): void {
    try {
      this.stripe = this.stripeCardService.getStripe(publishableKey);
    } catch (error: any) {
      this.mountError = error?.message || 'Unable to load Stripe.';
      return;
    }
    if (!this.cardElementHost) {
      // The host div (behind an *ngIf) can take an extra render pass or two to appear — most
      // visibly when this component sits inside app-stripe-add-card-button's p-dialog, which has
      // its own show animation. Retry a few times before giving up instead of failing silently.
      if (attempt >= 10) {
        this.mountError = 'Unable to load the card form. Please close and try again.';
        return;
      }
      setTimeout(() => this.mountCardElement(publishableKey, attempt + 1), 50);
      return;
    }

    this.elements = this.stripe.elements();
    this.cardElement = this.elements.create('card', {
      // This form only ever collects a fresh card + confirms a SetupIntent — it doesn't handle
      // Link's email/OTP handoff, so its autofill badge has no working flow behind it here and
      // only crowds the field visually against the brand icon + digits.
      disableLink: true,
      style: {
        base: {
          fontSize: '15px',
          lineHeight: '24px',
          color: '#101828',
          fontFamily: 'inherit',
          '::placeholder': { color: '#98a2b3' }
        },
        invalid: { color: '#e11d48' }
      }
    });
    this.cardElement.mount(this.cardElementHost.nativeElement);
    this.cardElement.on('change', (event: any) => {
      this.isCardComplete = !!event.complete;
      this.cardErrorText = event.error?.message || '';
      this.cardBrand = event.brand || 'unknown';
    });
    this.cardElement.on('focus', () => { this.isCardFocused = true; });
    this.cardElement.on('blur', () => { this.isCardFocused = false; });
  }

  get cardBrandLabel(): string {
    const labels: { [brand: string]: string } = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'Amex',
      discover: 'Discover',
      diners: 'Diners',
      jcb: 'JCB',
      unionpay: 'UnionPay'
    };
    return labels[this.cardBrand] || '';
  }

  get canSave(): boolean {
    return this.isCardComplete && !!this.fullName.trim() && !this.isSaving && !this.isSaved && !this.isInitializing && !this.mountError;
  }

  saveCard(): void {
    if (!this.canSave || !this.stripe || !this.cardElement || !this.clientSecret) return;

    this.isSaving = true;
    this.cardErrorText = '';
    this.stripe
      .confirmCardSetup(this.clientSecret, {
        payment_method: {
          card: this.cardElement,
          billing_details: {
            name: this.fullName.trim(),
            address: this.addressLine1.trim() ? { line1: this.addressLine1.trim() } : undefined
          }
        }
      })
      .then((result: any) => {
        if (result.error) {
          this.isSaving = false;
          this.cardErrorText = result.error.message || 'Your card details could not be validated.';
          // A failed confirm can leave this SetupIntent in a state Stripe won't accept another
          // confirm against — retrying with the same clientSecret would just repeat the same
          // failure. Fetch a fresh one in the background so the next "Save card" click has a
          // usable intent instead of hammering an already-spent one.
          this.refreshClientSecretAfterFailure();
          return;
        }

        const paymentMethodId = result.setupIntent?.payment_method;
        this.stripeCardService.attachPaymentMethod(paymentMethodId).subscribe({
          next: (attachResult) => {
            this.isSaving = false;
            if (!attachResult.success) {
              // The card is already attached to the Stripe customer at this point (confirmCardSetup
              // succeeded) — this failure is only the backend record of it, so don't silently claim
              // success, but the merchant doesn't need to re-enter card details to retry.
              this.cardErrorText = attachResult.message || 'Your card was verified but could not be saved. Please try again.';
              return;
            }
            this.isSaved = true;
            this.cardSaved.emit({
              paymentMethodId,
              cardBrand: attachResult.data?.cardBrand,
              cardLast4: attachResult.data?.cardLast4,
              cardExpMonth: attachResult.data?.cardExpMonth,
              cardExpYear: attachResult.data?.cardExpYear
            });
          },
          error: () => {
            this.isSaving = false;
            this.cardErrorText = 'Your card was verified but could not be saved. Please try again.';
          }
        });
      });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  /** Only replaces this.clientSecret — the mounted card Element must stay untouched (re-mounting
   *  it mid-retry isn't safe, same reasoning as the skeleton-overlay comment on the template) so
   *  the merchant doesn't lose the card details they already typed. */
  private refreshClientSecretAfterFailure(): void {
    this.stripeCardService.createSetupIntent().subscribe({
      next: (result) => {
        if (result.success && result.data?.clientSecret) {
          this.clientSecret = result.data.clientSecret;
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.cardElement?.destroy();
  }
}
